/**
 * LegalPage - Terms & Conditions (/terms), Privacy Policy (/privacy) and
 * Refund Policy (/refund-policy). Public: no sign-in needed. The wording lives
 * in config/legalContent.ts and the site's settings in config/legalConfig.ts.
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LEGAL, LEGAL_STYLE, TERMS_UPDATED } from '@/config/legalConfig'
import { legalDoc, type LegalKind } from '@/config/legalContent'

const NAV: [LegalKind, string, string][] = [
  ['terms', '/terms', 'Terms & Conditions'],
  ['privacy', '/privacy', 'Privacy Policy'],
  ['refunds', '/refund-policy', 'Refund Policy'],
]

export function LegalPage({ kind }: { kind: LegalKind }) {
  const doc = legalDoc(kind)

  useEffect(() => {
    const before = document.title
    document.title = `${doc.title} · ${LEGAL.siteTitle}`
    window.scrollTo(0, 0)
    return () => { document.title = before }
  }, [doc.title])

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:py-12" data-testid={`legal-page-${kind}`}>
      <article className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-sm sm:px-10 sm:py-10">
        <Link to="/" className="text-sm font-medium text-gray-500 hover:text-gray-800">
          ← {LEGAL.siteTitle}
        </Link>

        <nav className="mt-5 flex flex-wrap gap-2" aria-label="Policies">
          {NAV.map(([k, href, label]) => (
            <Link
              key={k}
              to={href}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${k === kind ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <h1 className="mt-6 text-3xl font-bold text-gray-900">{doc.title}</h1>
        <p className="mt-1 text-sm text-gray-500">Last updated: {TERMS_UPDATED}</p>

        <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-gray-700">
          {doc.intro.map((p) => <p key={p}>{p}</p>)}
        </div>

        <div className="mt-8 space-y-8">
          {doc.sections.map((s, i) => (
            <section key={s.title}>
              <h2 className="text-lg font-bold text-gray-900">{i + 1}. {s.title}</h2>
              <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-gray-700">
                {(s.paras || []).map((p) => <p key={p}>{p}</p>)}
                {s.bullets && (
                  <ul className="list-disc space-y-1.5 pl-5">
                    {s.bullets.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-10 border-t border-gray-200 pt-5 text-sm text-gray-600">
          Contact: <a href={`mailto:${LEGAL.supportEmail}`} className={LEGAL_STYLE.link}>{LEGAL.supportEmail}</a>
          <span className="mx-2 text-gray-300">·</span>
          {LEGAL.domain}
        </footer>
      </article>
    </div>
  )
}

export default LegalPage
